import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Chip,
    Grid,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Skeleton,
    InputAdornment,
    IconButton,
    Switch,
    FormControlLabel,
} from '@mui/material';
import {
    AccountBalance as AccountBalanceIcon,
    AccountBalanceWallet as WalletIcon,
    CheckCircle as CheckCircleIcon,
    Refresh as RefreshIcon,
    ArrowBack as ArrowBackIcon,
    PhoneAndroid as PhoneIcon,
    CreditScore as CreditScoreIcon,
    MonetizationOn as MoneyIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import CryptoJS from 'crypto-js';
import { KEY, ApiDetails } from '../../dummyData';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import DisabledFeatureBanner from '../../components/FeatureGate/DisabledFeatureBanner';

const MF_BASE = `${ApiDetails}pegasus/visionary/microfinance/`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getTenantDetails() {
    try {
        const encrypted = localStorage.getItem('tenant_details');
        if (!encrypted) return null;
        const decrypted = CryptoJS.AES.decrypt(encrypted, KEY);
        const text = decrypted.toString(CryptoJS.enc.Utf8);
        if (!text) return null;
        return new Map(Object.entries(JSON.parse(text)));
    } catch {
        return null;
    }
}

const ksh = (amount) =>
    `KES ${Number(amount || 0).toLocaleString('en-KE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-KE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
    PENDING:             { color: '#92400e', bg: '#fef3c7', borderColor: '#f59e0b', label: 'Under Review',         icon: '⏳' },
    PENDING_FEE_PAYMENT: { color: '#854d0e', bg: '#fef9c3', borderColor: '#eab308', label: 'Awaiting Fee Payment', icon: '💳' },
    APPROVED:            { color: '#1e40af', bg: '#dbeafe', borderColor: '#3b82f6', label: 'Approved',             icon: '✅' },
    REJECTED:            { color: '#991b1b', bg: '#fee2e2', borderColor: '#ef4444', label: 'Rejected',             icon: '✗' },
    DISBURSED:           { color: '#065f46', bg: '#d1fae5', borderColor: '#10b981', label: 'Disbursed',            icon: '💰' },
    PAID:                { color: '#374151', bg: '#f3f4f6', borderColor: '#9ca3af', label: 'Paid',                 icon: '✓' },
    OVERDUE:             { color: '#991b1b', bg: '#fee2e2', borderColor: '#ef4444', label: 'Overdue',              icon: '⚠️' },
    DEFAULTED:           { color: '#7f1d1d', bg: '#fca5a5', borderColor: '#b91c1c', label: 'Defaulted',           icon: '✗' },
    EXPIRED:             { color: '#6b7280', bg: '#f3f4f6', borderColor: '#d1d5db', label: 'Expired',             icon: '⏱' },
    CANCELLED:           { color: '#6b7280', bg: '#f3f4f6', borderColor: '#d1d5db', label: 'Cancelled',           icon: '✗' },
};

const RECOMMENDATION_MESSAGES = {
    APPROVE_FULL: 'Your credit profile looks great! Your application is under review.',
    APPROVE_PARTIAL: 'Your application is under review. You may be approved for a partial amount.',
    REVIEW: 'Your application has been submitted for manual review.',
    REJECT: 'Your credit score is currently low. Consider applying for a smaller amount.',
};

// ─── KYC config ────────────────────────────────────────────────────────────

const KYC_SLOTS_CONFIG = [
    { type: 'ID_FRONT',       label: 'National ID — Front',    hint: 'Clear photo of the front of your National ID',  canUseExisting: true },
    { type: 'ID_BACK',        label: 'National ID — Back',     hint: 'Clear photo of the back of your National ID',   canUseExisting: true },
    { type: 'PASSPORT_PHOTO', label: 'Passport Photo / Selfie',hint: 'A clear selfie or passport-sized photo',        canUseExisting: true },
    { type: 'PROOF_OF_INCOME',label: 'Proof of Income',        hint: 'e.g. payslip, bank statement, or business permit', canUseExisting: false },
];

const KYC_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
const KYC_MAX_SIZE = 10 * 1024 * 1024; // 10 MB

// ─── Main Component ───────────────────────────────────────────────────────────

export default function LoansPage() {
    const { isEnabled, getMessage } = useFeatureFlags();
    const tenantDetails = getTenantDetails();
    const tenantId = tenantDetails?.get('tenant_id');
    const accessToken = tenantDetails?.get('access_token');
    const tenantPhone = tenantDetails?.get('phone_number') || '';
    const tenantEmail = tenantDetails?.get('email_address') || '';
    const firstName = tenantDetails?.get('first_name') || '';
    const lastName = tenantDetails?.get('last_name') || '';

    // ── View state ─────────────────────────────────────────────────────────────
    // Possible values:
    //   loading | error | register_prompt | register_form | kyc_upload |
    //   loans_list | apply_product | apply_form | apply_success | pay_fee |
    //   loan_details | repay_waiting
    const [view, setView] = useState('loading');

    // ── Data states ────────────────────────────────────────────────────────────
    const [loansData, setLoansData] = useState(null);
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedLoan, setSelectedLoan] = useState(null);
    const [loanDetails, setLoanDetails] = useState(null);
    const [appliedLoan, setAppliedLoan] = useState(null);
    const [repayResult, setRepayResult] = useState(null);

    // ── KYC states ─────────────────────────────────────────────────────────────
    const [kycSlots, setKycSlots] = useState(
        KYC_SLOTS_CONFIG.map((s) => ({
            ...s,
            existing: false,
            file: null,
            mode: s.canUseExisting ? 'use_existing' : 'upload',
            status: 'idle', // idle | uploading | done | error
            error: null,
        }))
    );
    const [kycSubmitting, setKycSubmitting] = useState(false);
    const [kycOverallStatus, setKycOverallStatus] = useState(null); // null | PENDING | VERIFIED | REJECTED

    // ── Form states ────────────────────────────────────────────────────────────
    const [regForm, setRegForm] = useState({
        // required
        dateOfBirth: '',
        customerType: '',
        password: '',
        confirmPassword: '',
        nationalId: '',
        // general optional
        gender: '',
        residentialAddress: '',
        county: '',
        subCounty: '',
        mpesaNumber: '',
        bankName: '',
        bankAccountNumber: '',
        hasActiveFuliza: false,
        // SALARIED optional
        employerName: '',
        employerPhone: '',
        employmentStartDate: '',
        monthlyIncome: '',
        // BUSINESS optional
        businessName: '',
        businessType: '',
        businessRegistrationNumber: '',
        // reference contact (any type)
        referenceName: '',
        referencePhone: '',
        referenceRelationship: '',
        referenceEmail: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [loanForm, setLoanForm] = useState({ amount: '', tenor: '', purpose: '' });
    const [repayAmount, setRepayAmount] = useState('');
    const [repayDialog, setRepayDialog] = useState(false);

    // ── Fee payment states ─────────────────────────────────────────────────────
    const [feePaymentData, setFeePaymentData] = useState(null);  // apply response when fee_payment_required
    const [retriggerCooldown, setRetriggerCooldown] = useState(0); // seconds remaining on cooldown
    const [payFeeNow, setPayFeeNow] = useState(Date.now());        // updated every minute for countdown

    // ── UI states ──────────────────────────────────────────────────────────────
    const [loading, setLoading] = useState(false);
    const [pageError, setPageError] = useState(null);
    const [formAlert, setFormAlert] = useState(null); // { type, message }

    // ── Live loan preview (client-side calculation) ────────────────────────────
    const loanPreview = (() => {
        if (!selectedProduct || !loanForm.amount || !loanForm.tenor) return null;
        const amount = parseFloat(loanForm.amount);
        const tenor = parseInt(loanForm.tenor);
        if (isNaN(amount) || isNaN(tenor) || amount <= 0) return null;

        let interestRate;
        if (tenor === 1) interestRate = selectedProduct.interestRate1Week || 5;
        else if (tenor === 2) interestRate = selectedProduct.interestRate2Weeks || 7;
        else interestRate = selectedProduct.interestRate1Month || 10;

        const feeRate = parseFloat(selectedProduct.processingFeePercentage) || 2;
        const processingFee = (amount * feeRate) / 100;
        const interest = (amount * interestRate) / 100;
        const upfront = Boolean(selectedProduct.processingFeeUpfront);
        const youReceive = upfront ? amount : amount - processingFee;
        const youRepay = amount + interest;

        return { interestRate, feeRate, processingFee, interest, youReceive, youRepay, upfront };
    })();

    // ── API helper ─────────────────────────────────────────────────────────────
    const apiFetch = useCallback(
        async (path, method = 'POST', body = null) => {
            const opts = {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    token: accessToken || '',
                },
            };
            if (body && method !== 'GET') opts.body = JSON.stringify(body);
            const res = await fetch(MF_BASE + path, opts);
            const data = await res.json();
            return { ok: res.ok, status: res.status, data };
        },
        [accessToken]
    );

    // ── Load loans (initial + refresh) ────────────────────────────────────────
    const loadLoans = useCallback(async () => {
        setLoading(true);
        setPageError(null);
        try {
            const { ok, status, data } = await apiFetch('loans', 'POST', { tenant_id: tenantId });
            if (!ok) {
                if (status === 401) {
                    setPageError('Your session has expired. Please log in again.');
                } else {
                    setPageError(data.error || 'Failed to load loan information. Please try again.');
                }
                setView('error');
                return;
            }
            setLoansData(data);
            setView(data.registered ? 'loans_list' : 'register_prompt');
        } catch {
            setPageError('Could not connect to the server. Please check your connection and try again.');
            setView('error');
        } finally {
            setLoading(false);
        }
    }, [apiFetch, tenantId]);

    useEffect(() => {
        loadLoans();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Refresh KYC status whenever the kyc_upload view becomes active
    useEffect(() => {
        if (view === 'kyc_upload') {
            loadKycStatus();
        }
    }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

    // Retrigger cooldown: tick down one second at a time
    useEffect(() => {
        if (retriggerCooldown <= 0) return;
        const t = setTimeout(() => setRetriggerCooldown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [retriggerCooldown]);

    // Clock tick: update payFeeNow every minute so countdown re-renders
    useEffect(() => {
        if (view !== 'pay_fee') return;
        const interval = setInterval(() => setPayFeeNow(Date.now()), 60000);
        return () => clearInterval(interval);
    }, [view]);

    // Polling: check fee-payment status every 15 seconds
    useEffect(() => {
        if (view !== 'pay_fee' || !feePaymentData) return;
        const interval = setInterval(async () => {
            try {
                const { ok, data } = await apiFetch('loan/details', 'POST', {
                    tenant_id: tenantId,
                    mf_application_id: feePaymentData.application_id,
                });
                if (!ok) return;
                const status = data.local_status || data.application?.status;
                if (status === 'PENDING') {
                    clearInterval(interval);
                    setAppliedLoan(feePaymentData);
                    setView('apply_success');
                } else if (status === 'EXPIRED') {
                    clearInterval(interval);
                    setFormAlert({ type: 'warning', message: 'Your application has expired because the fee was not paid in time. Please apply again.' });
                    loadLoans();
                }
            } catch { /* ignore polling errors */ }
        }, 15000);
        return () => clearInterval(interval);
    }, [view, feePaymentData]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Utility: can tenant apply for a new loan? ──────────────────────────────
    const canApply = () => {
        if (!loansData?.loans?.length) return true;
        return !loansData.loans.some((l) =>
            ['PENDING', 'APPROVED', 'DISBURSED', 'PENDING_FEE_PAYMENT'].includes(l.status)
        );
    };

    // ── Load products ──────────────────────────────────────────────────────────
    const handleLoadProducts = async () => {
        setLoading(true);
        setFormAlert(null);
        try {
            const { ok, data } = await apiFetch('products', 'GET');
            if (!ok) {
                setFormAlert({ type: 'error', message: data.error || 'Could not load loan products.' });
                setLoading(false);
                return;
            }
            setProducts(data.products || []);
            setView('apply_product');
        } catch {
            setFormAlert({ type: 'error', message: 'Connection error. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    // ── Load KYC documents ─────────────────────────────────────────────────────
    // Checks existing docs; if VERIFIED → go to products; else show kyc_upload view.
    const loadKycDocuments = async () => {
        setLoading(true);
        setFormAlert(null);
        try {
            const { ok, status, data } = await apiFetch('kyc/documents', 'POST', { tenant_id: tenantId });
            if (status === 404 || data?.message === 'not_registered') {
                setLoading(false);
                setView('register_prompt');
                return;
            }
            if (ok && data.kyc_status === 'VERIFIED') {
                setLoading(false);
                handleLoadProducts();
                return;
            }
            // API may return existing_documents as { ID_FRONT: true/false, ... }
            // or documents as an array [{documentType, ...}]. Handle both.
            const existingMap = ok && data.existing_documents ? data.existing_documents : {};
            const existingDocsArray = ok ? (data.documents || []) : [];
            setKycSlots(
                KYC_SLOTS_CONFIG.map((cfg) => {
                    const found = existingDocsArray.find((d) => d.documentType === cfg.type)
                        || (existingMap[cfg.type] ? { documentType: cfg.type } : null);
                    return {
                        ...cfg,
                        existing: Boolean(found),
                        file: null,
                        mode: found && cfg.canUseExisting ? 'use_existing' : 'upload',
                        status: 'idle',
                        error: null,
                    };
                })
            );
            setKycOverallStatus(null);
            setView('kyc_upload');
        } catch {
            setFormAlert({ type: 'error', message: 'Could not load KYC documents. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    // ── Upload a single KYC slot ───────────────────────────────────────────────
    const uploadSlot = async (slot) => {
        setKycSlots((prev) =>
            prev.map((s) => (s.type === slot.type ? { ...s, status: 'uploading', error: null } : s))
        );
        const formData = new FormData();
        formData.append('tenant_id', tenantId);
        formData.append('documentType', slot.type);
        if (slot.mode === 'use_existing') {
            formData.append('useExisting', 'true');
        } else {
            formData.append('document', slot.file);
        }
        try {
            const res = await fetch(`${MF_BASE}kyc/upload`, {
                method: 'POST',
                headers: { token: accessToken || '' },
                body: formData,
            });
            const data = await res.json();

            if (res.status === 404 && slot.mode === 'use_existing') {
                // No existing document on server — switch slot to manual upload
                setKycSlots((prev) =>
                    prev.map((s) =>
                        s.type === slot.type
                            ? { ...s, mode: 'upload', status: 'idle', error: 'No existing document found. Please upload a file.' }
                            : s
                    )
                );
                return false;
            }
            if (!res.ok) {
                const errMsg =
                    res.status === 502
                        ? 'Upload could not be completed. Please try again later.'
                        : data.error || 'Upload failed. Please try again.';
                setKycSlots((prev) =>
                    prev.map((s) => (s.type === slot.type ? { ...s, status: 'error', error: errMsg } : s))
                );
                return false;
            }
            setKycSlots((prev) =>
                prev.map((s) => (s.type === slot.type ? { ...s, status: 'done', error: null } : s))
            );
            return true;
        } catch {
            setKycSlots((prev) =>
                prev.map((s) =>
                    s.type === slot.type
                        ? { ...s, status: 'error', error: 'Connection error. Please try again.' }
                        : s
                )
            );
            return false;
        }
    };

    // ── Submit all KYC documents sequentially ─────────────────────────────────
    const handleKycSubmit = async () => {
        setKycSubmitting(true);
        setFormAlert(null);
        for (const slot of kycSlots) {
            if (slot.status === 'done') continue; // Already uploaded in a previous attempt
            if (slot.mode === 'upload' && !slot.file) {
                setFormAlert({ type: 'error', message: `Please select a file for "${slot.label}".` });
                setKycSubmitting(false);
                return;
            }
            const success = await uploadSlot(slot);
            if (!success) {
                setKycSubmitting(false);
                return; // Stop on first failure; user can retry
            }
        }
        setKycSubmitting(false);
        await loadKycStatus();
    };

    // ── Fetch live KYC status ──────────────────────────────────────────────────
    // Called on kyc_upload view load and after all uploads complete.
    // Drives overall status banner and per-slot rejection feedback.
    const loadKycStatus = async () => {
        try {
            const { ok, data } = await apiFetch('kyc/status', 'POST', { tenant_id: tenantId });
            if (!ok) return;
            const overall = data.overallKycStatus || null;
            setKycOverallStatus(overall);
            if (overall === 'VERIFIED') {
                handleLoadProducts();
                return;
            }
            if (Array.isArray(data.documents)) {
                setKycSlots((prev) =>
                    prev.map((slot) => {
                        const doc = data.documents.find((d) => d.documentType === slot.type);
                        if (!doc) return slot;
                        if (doc.status === 'REJECTED') {
                            return {
                                ...slot,
                                mode: 'upload',
                                file: null,
                                status: 'idle',
                                error: `Your ${slot.label} was rejected: ${doc.rejectionReason}. Please upload a clearer copy.`,
                            };
                        }
                        // PENDING or APPROVED — mark as done
                        return { ...slot, status: 'done', error: null };
                    })
                );
            }
        } catch {
            // Non-critical status refresh — fail silently
        }
    };

    // ── Open registration form ────────────────────────────────────────────────────
    // 1. Check /microfinance/profile — if already registered skip the form.
    // 2. Fetch the PMS tenant record to pre-fill National ID.
    const openRegisterForm = async () => {
        setLoading(true);
        try {
            // Step 1 — registration status check
            const { ok: profileOk, data: profileData } = await apiFetch('profile', 'POST', { tenant_id: tenantId });
            if (profileOk && profileData.success) {
                // Tenant already has an MF account — go straight to loans
                setLoading(false);
                loadLoans();
                return;
            }

            // Step 2 — fetch PMS tenant record to pre-fill national ID
            const res = await fetch(
                `${ApiDetails}pegasus/visionary/tenant/get/specific/tenant`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', token: accessToken || '' },
                    body: JSON.stringify({ tenant_id: tenantId }),
                }
            );
            if (res.ok) {
                const record = await res.json();
                const storedId = record.national_id ?? '';
                setRegForm((p) => ({ ...p, nationalId: String(storedId) }));
            }
        } catch {
            // Network error — show the form anyway; submit will catch issues
        } finally {
            setLoading(false);
        }
        setView('register_form');
    };

    // ── Registration ───────────────────────────────────────────────────────────
    const handleRegister = async () => {
        setLoading(true);
        setFormAlert(null);
        try {
            // ── Build payload ──────────────────────────────────────────────────
            const payload = {
                // always required
                tenant_id: tenantId,
                dateOfBirth: regForm.dateOfBirth,
                customerType: regForm.customerType,
                password: regForm.password,
                nationalId: regForm.nationalId.trim(),
                // boolean — always include
                hasActiveFuliza: regForm.hasActiveFuliza,
                // general optional — omit when blank
                ...(regForm.gender && { gender: regForm.gender }),
                ...(regForm.residentialAddress && { residentialAddress: regForm.residentialAddress }),
                ...(regForm.county && { county: regForm.county }),
                ...(regForm.subCounty && { subCounty: regForm.subCounty }),
                ...(regForm.mpesaNumber && { mpesaNumber: regForm.mpesaNumber }),
                ...(regForm.bankName && { bankName: regForm.bankName }),
                ...(regForm.bankAccountNumber && { bankAccountNumber: regForm.bankAccountNumber }),
                ...(regForm.referenceName && { referenceName: regForm.referenceName }),
                ...(regForm.referencePhone && { referencePhone: regForm.referencePhone }),
                ...(regForm.referenceRelationship && { referenceRelationship: regForm.referenceRelationship }),
                ...(regForm.referenceEmail && { referenceEmail: regForm.referenceEmail }),
            };

            // SALARIED-specific optional fields
            if (regForm.customerType === 'SALARIED') {
                if (regForm.monthlyIncome) payload.monthlyIncome = parseFloat(regForm.monthlyIncome) || 0;
                if (regForm.employerName) payload.employerName = regForm.employerName;
                if (regForm.employerPhone) payload.employerPhone = regForm.employerPhone;
                if (regForm.employmentStartDate) payload.employmentStartDate = regForm.employmentStartDate;
            }

            // BUSINESS-specific optional fields
            if (regForm.customerType === 'BUSINESS') {
                if (regForm.monthlyIncome) payload.monthlyIncome = parseFloat(regForm.monthlyIncome) || 0;
                if (regForm.businessName) payload.businessName = regForm.businessName;
                if (regForm.businessType) payload.businessType = regForm.businessType;
                if (regForm.businessRegistrationNumber) payload.businessRegistrationNumber = regForm.businessRegistrationNumber;
            }

            const { ok, status, data } = await apiFetch('register', 'POST', payload);

            if (status === 409) {
                setLoading(false);
                loadLoans();
                return;
            }
            if (status === 400 && data.requires_national_id) {
                setFormAlert({ type: 'warning', message: 'Please enter your National ID number to continue.' });
                setLoading(false);
                return;
            }
            if (!ok) {
                setFormAlert({
                    type: 'error',
                    message: data.error ||
                        (status === 502
                            ? 'Registration could not be completed. Please try again later.'
                            : 'Registration failed. Please try again.'),
                });
                setLoading(false);
                return;
            }
            // Success — proceed to KYC document upload
            setLoansData((prev) => ({ ...prev, registered: true }));
            setLoading(false);
            loadKycDocuments();
        } catch {
            setFormAlert({ type: 'error', message: 'Connection error. Please try again.' });
            setLoading(false);
        }
    };

    // ── Apply for a loan ───────────────────────────────────────────────────────
    const handleApply = async () => {
        setLoading(true);
        setFormAlert(null);
        try {
            const { ok, status, data } = await apiFetch('apply', 'POST', {
                tenant_id: tenantId,
                product_id: selectedProduct.productId,
                requested_amount: parseFloat(loanForm.amount),
                tenor_weeks: parseInt(loanForm.tenor),
                loan_purpose: loanForm.purpose,
            });
            if (status === 409) {
                setFormAlert({
                    type: 'warning',
                    message: 'You already have an active loan. Please repay it before applying for a new one.',
                });
                setLoading(false);
                return;
            }
            if (!ok) {
                setFormAlert({
                    type: 'error',
                    message: data.error || 'Application failed. Please try again.',
                });
                setLoading(false);
                return;
            }
            if (data.fee_payment_required) {
                // Upfront-fee product — STK push already sent; show fee payment screen
                setFeePaymentData(data);
                setPayFeeNow(Date.now());
                setView('pay_fee');
            } else {
                setAppliedLoan(data);
                setView('apply_success');
            }
        } catch {
            setFormAlert({ type: 'error', message: 'Connection error. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    // ── Retrigger fee payment (Resend M-Pesa prompt) ───────────────────────────
    const handleRetriggerFee = async () => {
        setRetriggerCooldown(60);
        setFormAlert(null);
        try {
            const { ok, data } = await apiFetch('retrigger-fee', 'POST', { tenant_id: tenantId });
            if (!ok) {
                setFormAlert({ type: 'error', message: data.error || 'Could not resend the M-Pesa prompt. Please try again.' });
            } else {
                setFormAlert({ type: 'success', message: 'A new prompt has been sent to your phone.' });
            }
        } catch {
            setFormAlert({ type: 'error', message: 'Connection error. Please try again.' });
        }
    };

    // ── View loan details ──────────────────────────────────────────────────────
    const handleViewDetails = async (loan) => {
        setSelectedLoan(loan);
        setLoading(true);
        setFormAlert(null);
        try {
            const { ok, data } = await apiFetch('loan/details', 'POST', {
                tenant_id: tenantId,
                mf_application_id: loan.mf_application_id,
            });
            if (!ok) {
                setFormAlert({ type: 'error', message: data.error || 'Could not load loan details.' });
                setLoading(false);
                return;
            }
            setLoanDetails(data);
            setView('loan_details');
        } catch {
            setFormAlert({ type: 'error', message: 'Connection error. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    // ── Repay ──────────────────────────────────────────────────────────────────
    const handleRepay = async () => {
        setRepayDialog(false);
        setLoading(true);
        setFormAlert(null);
        try {
            // Normalise phone to 254XXXXXXXXX
            const phoneFormatted = tenantPhone
                .replace(/^0/, '254')
                .replace(/\D/g, '');
            const { ok, status, data } = await apiFetch('repay', 'POST', {
                tenant_id: tenantId,
                mf_application_id: selectedLoan.mf_application_id,
                amount: parseFloat(repayAmount),
                phone_number: phoneFormatted,
            });
            if (!ok) {
                const msg =
                    status === 409
                        ? data.error || 'This loan cannot be repaid right now.'
                        : data.error || 'Could not initiate repayment. Please try again.';
                setFormAlert({ type: 'error', message: msg });
                setLoading(false);
                return;
            }
            setRepayResult(data);
            setView('repay_waiting');
        } catch {
            setFormAlert({ type: 'error', message: 'Connection error. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    // ─── Shared styles ─────────────────────────────────────────────────────────
    const S = {
        page: { padding: '24px', maxWidth: '860px', margin: '0 auto' },
        card: {
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            marginBottom: '16px',
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '24px',
        },
        backBtn: { minWidth: 'auto', padding: '8px', borderRadius: '8px' },
        actionBtn: { borderRadius: '8px', textTransform: 'none', fontWeight: 600 },
        statusChip: (status) => ({
            backgroundColor: STATUS_CONFIG[status]?.bg || '#f3f4f6',
            color: STATUS_CONFIG[status]?.color || '#6b7280',
            border: `1px solid ${STATUS_CONFIG[status]?.borderColor || '#e5e7eb'}`,
            fontWeight: 600,
            fontSize: '12px',
        }),
        profileBanner: {
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
            color: 'white',
            borderRadius: '12px',
            padding: '20px 24px',
            marginBottom: '20px',
        },
        loanCard: {
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
            marginBottom: '12px',
            transition: 'box-shadow 0.2s',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.14)' },
        },
        previewBox: {
            mt: 2,
            p: 2,
            backgroundColor: '#f0f9ff',
            borderRadius: 2,
            border: '1px solid #bae6fd',
        },
    };

    // ═══════════════════════════════════════════════════════════════════════════
    //  VIEWS
    // ═══════════════════════════════════════════════════════════════════════════

    if (!isEnabled('loans')) {
        return <DisabledFeatureBanner featureName="Loans" message={getMessage('loans')} />;
    }

    // ── Loading ────────────────────────────────────────────────────────────────
    if (view === 'loading') {
        return (
            <Box sx={S.page}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 2 }}>
                    <CircularProgress />
                    <Typography color="text.secondary">Loading your loan information…</Typography>
                </Box>
            </Box>
        );
    }

    // ── Error ──────────────────────────────────────────────────────────────────
    if (view === 'error') {
        return (
            <Box sx={S.page}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {pageError}
                </Alert>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadLoans}>
                    Try Again
                </Button>
            </Box>
        );
    }

    // ── Register prompt ────────────────────────────────────────────────────────
    if (view === 'register_prompt') {
        return (
            <Box sx={S.page}>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                    Microfinance Loans
                </Typography>
                <Card sx={S.card}>
                    <CardContent sx={{ textAlign: 'center', py: 5 }}>
                        <WalletIcon sx={{ fontSize: 68, color: '#2563eb', mb: 2 }} />
                        <Typography variant="h6" fontWeight={700} gutterBottom>
                            Get Access to Quick Loans
                        </Typography>
                        <Typography
                            color="text.secondary"
                            sx={{ mb: 4, maxWidth: 420, mx: 'auto' }}
                        >
                            Apply for short-term loans directly from the app. Money is disbursed
                            straight to your M-Pesa. Register once to get started — it only takes a
                            minute.
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 4,
                                justifyContent: 'center',
                                mb: 4,
                                flexWrap: 'wrap',
                            }}
                        >
                            {[
                                { icon: '⚡', text: 'Quick Approval' },
                                { icon: '📱', text: 'M-Pesa Disbursement' },
                                { icon: '🔒', text: 'Secure & Trusted' },
                            ].map((f) => (
                                <Box
                                    key={f.text}
                                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}
                                >
                                    <Typography fontSize={32}>{f.icon}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {f.text}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                        <Button
                            variant="contained"
                            size="large"
                            sx={{ ...S.actionBtn, px: 5, py: 1.5 }}
                            onClick={openRegisterForm}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                        >
                            {loading ? 'Checking…' : 'Register for Microfinance'}
                        </Button>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    // ── Registration form ──────────────────────────────────────────────────────
    if (view === 'register_form') {
        const pwRules = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
        const pwValid = pwRules.test(regForm.password);
        const pwMatch = regForm.confirmPassword === regForm.password && regForm.confirmPassword.length > 0;
        const isValid =
            regForm.dateOfBirth &&
            regForm.customerType &&
            pwValid &&
            pwMatch &&
            regForm.nationalId.trim().length > 0 &&
            (regForm.customerType !== 'SALARIED' || regForm.employerName.trim().length > 0) &&
            (regForm.customerType !== 'SALARIED' || regForm.employmentStartDate.trim().length > 0);

        const field = (key, props) => (
            <TextField
                value={regForm[key]}
                onChange={(e) => setRegForm((p) => ({ ...p, [key]: e.target.value }))}
                fullWidth
                {...props}
            />
        );

        return (
            <Box sx={S.page}>
                <Box sx={S.header}>
                    <Button sx={S.backBtn} onClick={() => setView('register_prompt')}>
                        <ArrowBackIcon />
                    </Button>
                    <Typography variant="h5" fontWeight={700}>
                        Create Microfinance Account
                    </Typography>
                </Box>

                {formAlert && (
                    <Alert severity={formAlert.type} onClose={() => setFormAlert(null)} sx={{ mb: 2 }}>
                        {formAlert.message}
                    </Alert>
                )}

                {/* ── Pre-filled details (read-only) + editable National ID ── */}
                <Card sx={S.card}>
                    <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Your details (from PMS profile)
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} sm={6}>
                                <TextField label="Full Name" value={`${firstName} ${lastName}`.trim()} fullWidth size="small" disabled />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField label="Phone Number" value={tenantPhone} fullWidth size="small" disabled />
                            </Grid>
                            {tenantEmail && (
                                <Grid item xs={12} sm={6}>
                                    <TextField label="Email Address" value={tenantEmail} fullWidth size="small" disabled />
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                {field('nationalId', {
                                    label: 'National ID Number *',
                                    required: true,
                                    placeholder: 'e.g. 37034104',
                                    helperText: 'Pre-filled from your profile — correct if needed',
                                })}
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* ── Account information (required fields) ── */}
                <Card sx={S.card}>
                    <CardContent>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            Account Information
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Date of Birth *"
                                    type="date"
                                    value={regForm.dateOfBirth}
                                    onChange={(e) => setRegForm((p) => ({ ...p, dateOfBirth: e.target.value }))}
                                    fullWidth
                                    required
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ max: new Date().toISOString().split('T')[0] }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Customer Type *</InputLabel>
                                    <Select
                                        value={regForm.customerType}
                                        label="Customer Type *"
                                        onChange={(e) => setRegForm((p) => ({ ...p, customerType: e.target.value }))}
                                    >
                                        <MenuItem value="SALARIED">Salaried Employee</MenuItem>
                                        <MenuItem value="BUSINESS">Business Owner</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            {/* Password */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Password *"
                                    type={showPassword ? 'text' : 'password'}
                                    value={regForm.password}
                                    onChange={(e) => setRegForm((p) => ({ ...p, password: e.target.value }))}
                                    fullWidth
                                    required
                                    error={Boolean(regForm.password && !pwValid)}
                                    helperText={
                                        regForm.password && !pwValid
                                            ? 'Min 8 chars, must include uppercase, lowercase, and digit'
                                            : 'Used to log in to the ALLENDCO Microfinance portal'
                                    }
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword((p) => !p)} edge="end" size="small">
                                                    {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            {/* Confirm password */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Confirm Password *"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={regForm.confirmPassword}
                                    onChange={(e) => setRegForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                                    fullWidth
                                    required
                                    error={Boolean(regForm.confirmPassword && !pwMatch)}
                                    helperText={regForm.confirmPassword && !pwMatch ? 'Passwords do not match' : ''}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowConfirmPassword((p) => !p)} edge="end" size="small">
                                                    {showConfirmPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            {/* Gender */}
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Gender</InputLabel>
                                    <Select
                                        value={regForm.gender}
                                        label="Gender"
                                        onChange={(e) => setRegForm((p) => ({ ...p, gender: e.target.value }))}
                                    >
                                        <MenuItem value="MALE">Male</MenuItem>
                                        <MenuItem value="FEMALE">Female</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* ── SALARIED fields ── */}
                {regForm.customerType === 'SALARIED' && (
                    <Card sx={S.card}>
                        <CardContent>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Employment Details{' '}
                                <Typography component="span" variant="caption" color="text.secondary">(employer name &amp; start date required)</Typography>
                            </Typography>
                            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                <Grid item xs={12} sm={6}>
                                    {field('employerName', { label: 'Employer Name *', required: true })}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {field('employerPhone', { label: 'Employer Phone', placeholder: '07XXXXXXXX' })}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Employment Start Date *"
                                        type="date"
                                        value={regForm.employmentStartDate}
                                        onChange={(e) => setRegForm((p) => ({ ...p, employmentStartDate: e.target.value }))}
                                        fullWidth
                                        required
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ max: new Date().toISOString().split('T')[0] }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {field('monthlyIncome', { label: 'Monthly Income (KES)', type: 'number', helperText: 'Helps with loan limit assessment' })}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                )}

                {/* ── BUSINESS fields ── */}
                {regForm.customerType === 'BUSINESS' && (
                    <Card sx={S.card}>
                        <CardContent>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Business Details{' '}
                                <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
                            </Typography>
                            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                <Grid item xs={12} sm={6}>
                                    {field('businessName', { label: 'Business Name' })}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {field('businessType', { label: 'Business Type', placeholder: 'e.g. Retail, Wholesale' })}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {field('businessRegistrationNumber', { label: 'Business Registration Number' })}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {field('monthlyIncome', { label: 'Monthly Income (KES)', type: 'number', helperText: 'Helps with loan limit assessment' })}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                )}

                {/* ── Location & contact ── */}
                <Card sx={S.card}>
                    <CardContent>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            Location &amp; Contact{' '}
                            <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} sm={6}>
                                {field('residentialAddress', { label: 'Residential Address' })}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {field('county', { label: 'County', placeholder: 'e.g. Nairobi' })}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {field('subCounty', { label: 'Sub-County', placeholder: 'e.g. Westlands' })}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {field('mpesaNumber', { label: 'M-Pesa Number', placeholder: '07XXXXXXXX', helperText: 'Defaults to your registered phone if left blank' })}
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* ── Banking details ── */}
                <Card sx={S.card}>
                    <CardContent>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            Banking Details{' '}
                            <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} sm={6}>
                                {field('bankName', { label: 'Bank Name', placeholder: 'e.g. KCB, Equity' })}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {field('bankAccountNumber', { label: 'Bank Account Number' })}
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={regForm.hasActiveFuliza}
                                            onChange={(e) => setRegForm((p) => ({ ...p, hasActiveFuliza: e.target.checked }))}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body2">I have an active Fuliza account</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Fuliza usage may affect your loan assessment
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* ── Reference contact ── */}
                <Card sx={S.card}>
                    <CardContent>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            Reference Contact{' '}
                            <Typography component="span" variant="caption" color="text.secondary">(recommended)</Typography>
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} sm={6}>
                                {field('referenceName', { label: 'Full Name' })}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {field('referencePhone', { label: 'Phone Number', placeholder: '07XXXXXXXX' })}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {field('referenceRelationship', { label: 'Relationship', placeholder: 'e.g. Spouse, Parent, Sibling' })}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {field('referenceEmail', { label: 'Email Address', type: 'email' })}
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={!isValid || loading}
                    onClick={handleRegister}
                    sx={{ ...S.actionBtn, py: 1.5 }}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                >
                    {loading ? 'Registering…' : 'Complete Registration'}
                </Button>
            </Box>
        );
    }

    // ── Loans list ─────────────────────────────────────────────────────────────
    if (view === 'loans_list') {
        const loans = loansData?.loans || [];
        const creditScore = loansData?.credit_score;
        const loanLimit = loansData?.loan_limit;
        const kycStatus = loansData?.kyc_status;
        const applyAllowed = canApply();

        return (
            <Box sx={S.page}>
                {/* Page title + refresh */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                    }}
                >
                    <Typography variant="h5" fontWeight={700}>
                        My Loans
                    </Typography>
                    <Button
                        size="small"
                        startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                        onClick={loadLoans}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                </Box>

                {formAlert && (
                    <Alert severity={formAlert.type} onClose={() => setFormAlert(null)} sx={{ mb: 2 }}>
                        {formAlert.message}
                    </Alert>
                )}

                {/* Pending fee payment banner */}
                {loans.some((l) => l.status === 'PENDING_FEE_PAYMENT') && (() => {
                    const pendingFee = loans.find((l) => l.status === 'PENDING_FEE_PAYMENT');
                    return (
                        <Alert
                            severity="warning"
                            sx={{ mb: 2, cursor: 'pointer' }}
                            onClick={() => handleViewDetails(pendingFee)}
                        >
                            You have a pending fee payment. Tap here to complete it and proceed with your loan application.
                        </Alert>
                    );
                })()}

                {/* KYC warning */}
                {kycStatus === 'UNVERIFIED' && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Your identity is pending verification. Loan applications may be delayed while
                        the back-office processes your KYC.
                    </Alert>
                )}

                {/* Profile header — credit score + loan limit */}
                {(creditScore || loanLimit) && (
                    <Box sx={S.profileBanner}>
                        <Typography variant="caption" sx={{ opacity: 0.75, display: 'block', mb: 1 }}>
                            Your Microfinance Profile
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <MoneyIcon sx={{ fontSize: 18, opacity: 0.8 }} />
                                    <Typography variant="caption" sx={{ opacity: 0.75 }}>
                                        Loan Limit
                                    </Typography>
                                </Box>
                                <Typography variant="h6" fontWeight={700}>
                                    {ksh(loanLimit)}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <CreditScoreIcon sx={{ fontSize: 18, opacity: 0.8 }} />
                                    <Typography variant="caption" sx={{ opacity: 0.75 }}>
                                        Credit Score
                                    </Typography>
                                </Box>
                                <Typography variant="h6" fontWeight={700}>
                                    {creditScore}/100
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* Active-loan blocker notice */}
                {!applyAllowed && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        You have an active loan. Please repay it before applying for a new one.
                    </Alert>
                )}

                {/* Empty state */}
                {loans.length === 0 ? (
                    <Card sx={S.card}>
                        <CardContent sx={{ textAlign: 'center', py: 5 }}>
                            <AccountBalanceIcon
                                sx={{ fontSize: 60, color: '#9ca3af', mb: 2 }}
                            />
                            <Typography variant="h6" gutterBottom>
                                No Loans Yet
                            </Typography>
                            <Typography color="text.secondary" sx={{ mb: 3 }}>
                                You haven't applied for any loans. Apply now to get started.
                            </Typography>
                            <Button
                                variant="contained"
                                sx={S.actionBtn}
                                onClick={() => kycStatus !== 'VERIFIED' ? loadKycDocuments() : handleLoadProducts()}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                            >
                                Apply for a Loan
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        {loans.map((loan) => {
                            const cfg = STATUS_CONFIG[loan.status] || STATUS_CONFIG.PENDING;
                            const canRepayLoan = ['DISBURSED', 'OVERDUE'].includes(loan.status);
                            const isExpired = loan.status === 'EXPIRED';
                            return (
                                <Card key={loan.id} sx={{ ...S.loanCard, opacity: isExpired ? 0.7 : 1 }}>
                                    <CardContent>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                mb: 1,
                                            }}
                                        >
                                            <Typography variant="h6" fontWeight={700}>
                                                {ksh(loan.principal)} Loan
                                            </Typography>
                                            <Chip
                                                label={`${cfg.icon} ${cfg.label}`}
                                                size="small"
                                                sx={S.statusChip(loan.status)}
                                            />
                                        </Box>

                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Repay {ksh(loan.total_repayment)} by {fmtDate(loan.due_date)}
                                        </Typography>

                                        <Typography variant="caption" color="text.secondary">
                                            Tenor: {loan.tenor_weeks} week
                                            {loan.tenor_weeks > 1 ? 's' : ''} &nbsp;|&nbsp; Interest:{' '}
                                            {loan.interest_rate}%
                                        </Typography>

                                        {loan.loan_purpose && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block' }}
                                            >
                                                Purpose: {loan.loan_purpose}
                                            </Typography>
                                        )}

                                        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                sx={S.actionBtn}
                                                onClick={() => handleViewDetails(loan)}
                                                disabled={loading}
                                            >
                                                View Details
                                            </Button>
                                            {canRepayLoan && (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    color={loan.status === 'OVERDUE' ? 'error' : 'primary'}
                                                    sx={S.actionBtn}
                                                    onClick={() => {
                                                        setSelectedLoan(loan);
                                                        setRepayAmount(String(loan.total_repayment));
                                                        setRepayDialog(true);
                                                    }}
                                                >
                                                    Repay Now
                                                </Button>
                                            )}
                                            {loan.status === 'PENDING_FEE_PAYMENT' && (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    color="warning"
                                                    sx={S.actionBtn}
                                                    onClick={() => handleViewDetails(loan)}
                                                    disabled={loading}
                                                >
                                                    Pay Fee
                                                </Button>
                                            )}
                                            {isExpired && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    sx={S.actionBtn}
                                                    onClick={() => kycStatus !== 'VERIFIED' ? loadKycDocuments() : handleLoadProducts()}
                                                    disabled={loading}
                                                >
                                                    Apply Again
                                                </Button>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            );
                        })}

                        {applyAllowed && (
                            <Button
                                variant="contained"
                                fullWidth
                                sx={{ ...S.actionBtn, mt: 1, py: 1.5 }}
                                onClick={() => kycStatus !== 'VERIFIED' ? loadKycDocuments() : handleLoadProducts()}
                                disabled={loading}
                                startIcon={
                                    loading ? <CircularProgress size={18} color="inherit" /> : null
                                }
                            >
                                Apply for a New Loan
                            </Button>
                        )}
                    </>
                )}

                {/* Repayment confirmation dialog */}
                <Dialog open={repayDialog} onClose={() => setRepayDialog(false)} maxWidth="xs" fullWidth>
                    <DialogTitle>Repay Loan</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            An M-Pesa STK Push will be sent to{' '}
                            <strong>{tenantPhone}</strong>. Enter the amount you wish to repay:
                        </Typography>
                        <TextField
                            label="Amount (KES)"
                            type="number"
                            value={repayAmount}
                            onChange={(e) => setRepayAmount(e.target.value)}
                            fullWidth
                            size="small"
                            helperText={
                                selectedLoan
                                    ? `Total outstanding: ${ksh(selectedLoan.total_repayment)}`
                                    : ''
                            }
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRepayDialog(false)}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={handleRepay}
                            disabled={!repayAmount || parseFloat(repayAmount) <= 0 || loading}
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            Send STK Push
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    // ── Product selection ──────────────────────────────────────────────────────
    if (view === 'apply_product') {
        const activeProducts = products.filter((p) => p.isActive);
        return (
            <Box sx={S.page}>
                <Box sx={S.header}>
                    <Button sx={S.backBtn} onClick={() => setView('loans_list')}>
                        <ArrowBackIcon />
                    </Button>
                    <Typography variant="h5" fontWeight={700}>
                        Choose a Loan Product
                    </Typography>
                </Box>

                {formAlert && (
                    <Alert severity={formAlert.type} onClose={() => setFormAlert(null)} sx={{ mb: 2 }}>
                        {formAlert.message}
                    </Alert>
                )}

                {loading ? (
                    [1, 2].map((i) => (
                        <Skeleton key={i} variant="rectangular" height={140} sx={{ borderRadius: 2, mb: 2 }} />
                    ))
                ) : activeProducts.length === 0 ? (
                    <Alert severity="info">
                        No loan products are available at this time. Please check back later.
                    </Alert>
                ) : (
                    activeProducts.map((product) => (
                        <Card
                            key={product.productId}
                            sx={{
                                ...S.loanCard,
                                cursor: 'pointer',
                                border: '2px solid transparent',
                                '&:hover': {
                                    border: '2px solid #2563eb',
                                    boxShadow: '0 4px 16px rgba(37,99,235,0.15)',
                                },
                            }}
                            onClick={() => {
                                setSelectedProduct(product);
                                setLoanForm({ amount: '', tenor: '', purpose: '' });
                                setView('apply_form');
                            }}
                        >
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                    <Typography variant="h6" fontWeight={700}>
                                        {product.productName}
                                    </Typography>
                                    {product.processingFeeUpfront && (
                                        <Chip
                                            label="Fee via M-Pesa"
                                            size="small"
                                            sx={{ backgroundColor: '#fef9c3', color: '#854d0e', border: '1px solid #eab308', fontWeight: 600, fontSize: '11px' }}
                                        />
                                    )}
                                </Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Loan Range: {ksh(product.minAmount)} — {ksh(product.maxAmount)}
                                </Typography>
                                {product.processingFeeUpfront && (
                                    <Alert severity="warning" sx={{ py: 0.5, mb: 1, fontSize: '13px' }}>
                                        Requires a <strong>{product.processingFeePercentage}% processing fee</strong> paid via M-Pesa <strong>before</strong> review. You receive the full loan amount.
                                    </Alert>
                                )}
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Interest rates by repayment period:
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={4}>
                                        <Typography variant="caption" color="text.secondary">
                                            1 Week
                                        </Typography>
                                        <Typography variant="body2" fontWeight={700}>
                                            {product.interestRate1Week}%
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="caption" color="text.secondary">
                                            2 Weeks
                                        </Typography>
                                        <Typography variant="body2" fontWeight={700}>
                                            {product.interestRate2Weeks}%
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="caption" color="text.secondary">
                                            4 Weeks
                                        </Typography>
                                        <Typography variant="body2" fontWeight={700}>
                                            {product.interestRate1Month}%
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mt: 1, display: 'block' }}
                                >
                                    Processing fee: {product.processingFeePercentage}%
                                </Typography>
                            </CardContent>
                        </Card>
                    ))
                )}
            </Box>
        );
    }

    // ── Loan application form ──────────────────────────────────────────────────
    if (view === 'apply_form') {
        const amountNum = parseFloat(loanForm.amount);
        const amountValid =
            !loanForm.amount ||
            (amountNum >= (selectedProduct?.minAmount || 0) &&
                amountNum <= (selectedProduct?.maxAmount || Infinity));
        const formValid = loanForm.amount && loanForm.tenor && amountValid;

        return (
            <Box sx={S.page}>
                <Box sx={S.header}>
                    <Button sx={S.backBtn} onClick={() => setView('apply_product')}>
                        <ArrowBackIcon />
                    </Button>
                    <Typography variant="h5" fontWeight={700}>
                        Loan Application
                    </Typography>
                </Box>

                {formAlert && (
                    <Alert severity={formAlert.type} onClose={() => setFormAlert(null)} sx={{ mb: 2 }}>
                        {formAlert.message}
                    </Alert>
                )}

                <Card sx={S.card}>
                    <CardContent>
                        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                            Product: <strong>{selectedProduct?.productName}</strong>
                        </Typography>

                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Loan Amount (KES)"
                                    type="number"
                                    value={loanForm.amount}
                                    onChange={(e) =>
                                        setLoanForm((p) => ({ ...p, amount: e.target.value }))
                                    }
                                    fullWidth
                                    error={Boolean(loanForm.amount && !amountValid)}
                                    helperText={
                                        loanForm.amount && !amountValid
                                            ? `Must be between ${ksh(selectedProduct?.minAmount)} and ${ksh(selectedProduct?.maxAmount)}`
                                            : `Min: ${ksh(selectedProduct?.minAmount)} / Max: ${ksh(selectedProduct?.maxAmount)}`
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Repayment Period</InputLabel>
                                    <Select
                                        value={loanForm.tenor}
                                        label="Repayment Period"
                                        onChange={(e) =>
                                            setLoanForm((p) => ({ ...p, tenor: e.target.value }))
                                        }
                                    >
                                        <MenuItem value="1">
                                            1 Week — {selectedProduct?.interestRate1Week}% interest
                                        </MenuItem>
                                        <MenuItem value="2">
                                            2 Weeks — {selectedProduct?.interestRate2Weeks}% interest
                                        </MenuItem>
                                        <MenuItem value="4">
                                            4 Weeks — {selectedProduct?.interestRate1Month}% interest
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Loan Purpose (optional)"
                                    value={loanForm.purpose}
                                    onChange={(e) =>
                                        setLoanForm((p) => ({ ...p, purpose: e.target.value }))
                                    }
                                    fullWidth
                                    placeholder="e.g. Rent top-up, Business, Emergency"
                                />
                            </Grid>
                        </Grid>

                        {/* Upfront-fee notice */}
                        {selectedProduct?.processingFeeUpfront && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                This product requires a <strong>{selectedProduct.processingFeePercentage}% processing fee</strong> paid via M-Pesa <strong>before</strong> your application is reviewed. You will receive the full loan amount upon disbursement.
                            </Alert>
                        )}

                        {/* Live preview */}
                        {loanPreview && (
                            <Box sx={S.previewBox}>
                                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                    Loan Summary
                                </Typography>
                                <Grid container spacing={0.5}>
                                    <Grid item xs={7}>
                                        <Typography variant="body2" color="text.secondary">
                                            Interest ({loanPreview.interestRate}%)
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Typography variant="body2" textAlign="right">
                                            {ksh(loanPreview.interest)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={7}>
                                        <Typography
                                            variant="body2"
                                            color={loanPreview.upfront ? 'warning.main' : 'text.secondary'}
                                            fontWeight={loanPreview.upfront ? 600 : 400}
                                        >
                                            {loanPreview.upfront
                                                ? `Fee you pay via M-Pesa (${loanPreview.feeRate}%)`
                                                : `Processing Fee (${loanPreview.feeRate}%) — deducted`}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Typography
                                            variant="body2"
                                            textAlign="right"
                                            color={loanPreview.upfront ? 'warning.main' : 'inherit'}
                                            fontWeight={loanPreview.upfront ? 600 : 400}
                                        >
                                            {ksh(loanPreview.processingFee)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                    </Grid>
                                    <Grid item xs={7}>
                                        <Typography variant="body2" fontWeight={700} color="success.main">
                                            You receive{loanPreview.upfront ? ' (full amount)' : ''}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Typography variant="body2" fontWeight={700} color="success.main" textAlign="right">
                                            {ksh(loanPreview.youReceive)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={7}>
                                        <Typography variant="body2" fontWeight={700} color="error.main">
                                            You repay
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Typography variant="body2" fontWeight={700} color="error.main" textAlign="right">
                                            {ksh(loanPreview.youRepay)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={!formValid || loading}
                    onClick={handleApply}
                    sx={{ ...S.actionBtn, py: 1.5 }}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                >
                    {loading ? 'Submitting…' : 'Submit Application'}
                </Button>
            </Box>
        );
    }

    // ── Application success ────────────────────────────────────────────────────
    if (view === 'apply_success') {
        const recMsg =
            RECOMMENDATION_MESSAGES[appliedLoan?.recommendation] || RECOMMENDATION_MESSAGES.REVIEW;

        return (
            <Box sx={S.page}>
                <Box sx={{ textAlign: 'center', py: 5 }}>
                    <CheckCircleIcon sx={{ fontSize: 76, color: '#10b981', mb: 2 }} />
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                        Application Submitted!
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 440, mx: 'auto' }}>
                        {recMsg}
                    </Typography>

                    <Card sx={{ ...S.card, textAlign: 'left', maxWidth: 420, mx: 'auto', mb: 3 }}>
                        <CardContent>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                Application Summary
                            </Typography>
                            <Grid container spacing={0.75}>
                                <Grid item xs={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        Principal
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography variant="body2" textAlign="right">
                                        {ksh(appliedLoan?.principal)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        You will receive
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography variant="body2" textAlign="right">
                                        {ksh(appliedLoan?.disbursement_amount)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        Interest rate
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography variant="body2" textAlign="right">
                                        {appliedLoan?.interest_rate}%
                                    </Typography>
                                </Grid>
                                <Grid item xs={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        Tenor
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography variant="body2" textAlign="right">
                                        {appliedLoan?.tenor_weeks} week(s)
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                </Grid>
                                <Grid item xs={7}>
                                    <Typography variant="body2" fontWeight={700}>
                                        Total to repay
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography variant="body2" fontWeight={700} textAlign="right">
                                        {ksh(appliedLoan?.total_repayment)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3, maxWidth: 420, mx: 'auto' }}
                    >
                        A loan officer will review your application. You'll be notified once it's
                        approved and the money is sent to your M-Pesa.
                    </Typography>

                    <Button
                        variant="contained"
                        sx={S.actionBtn}
                        onClick={() => loadLoans()}
                    >
                        View My Loans
                    </Button>
                </Box>
            </Box>
        );
    }

    // ── Loan details ───────────────────────────────────────────────────────────
    if (view === 'loan_details') {
        const loan = loanDetails?.loan;
        const app = loanDetails?.application;
        const status = loanDetails?.local_status || selectedLoan?.status;
        const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
        const canRepayLoan = ['DISBURSED', 'OVERDUE'].includes(status);

        return (
            <Box sx={S.page}>
                <Box sx={S.header}>
                    <Button sx={S.backBtn} onClick={() => setView('loans_list')}>
                        <ArrowBackIcon />
                    </Button>
                    <Typography variant="h5" fontWeight={700}>
                        Loan Details
                    </Typography>
                    <Chip
                        label={`${cfg.icon} ${cfg.label}`}
                        size="small"
                        sx={{ ml: 'auto', ...S.statusChip(status) }}
                    />
                </Box>

                {formAlert && (
                    <Alert severity={formAlert.type} onClose={() => setFormAlert(null)} sx={{ mb: 2 }}>
                        {formAlert.message}
                    </Alert>
                )}

                {/* Application section — always shown */}
                {app && (
                    <Card sx={S.card}>
                        <CardContent>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                Application
                            </Typography>
                            <Grid container spacing={0.75}>
                                <Grid item xs={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        Requested Amount
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography variant="body2" textAlign="right">
                                        {ksh(app.requestedAmount)}
                                    </Typography>
                                </Grid>
                                {app.approvedAmount && (
                                    <>
                                        <Grid item xs={7}>
                                            <Typography variant="body2" color="text.secondary">
                                                Approved Amount
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={5}>
                                            <Typography variant="body2" textAlign="right">
                                                {ksh(app.approvedAmount)}
                                            </Typography>
                                        </Grid>
                                    </>
                                )}
                                <Grid item xs={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        Tenor
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography variant="body2" textAlign="right">
                                        {app.tenorWeeks} week(s)
                                    </Typography>
                                </Grid>
                                <Grid item xs={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        Credit Score at Application
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography variant="body2" textAlign="right">
                                        {app.creditScoreAtApplication}/100
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                )}

                {/* Financial breakdown — only available after disbursement */}
                {loan && (
                    <Card sx={S.card}>
                        <CardContent>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                Financials
                            </Typography>
                            <Grid container spacing={0.75}>
                                <Grid item xs={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        Principal
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography variant="body2" textAlign="right">
                                        {ksh(loan.principalAmount)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        Processing Fee
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography variant="body2" textAlign="right">
                                        {ksh(loan.processingFee)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        Interest
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography variant="body2" textAlign="right">
                                        {ksh(loan.interestAmount)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        Amount Received (M-Pesa)
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography variant="body2" textAlign="right">
                                        {ksh(loan.principalAmount - loan.processingFee)}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                </Grid>

                                <Grid item xs={7}>
                                    <Typography variant="body2" fontWeight={700}>
                                        Total Due
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography variant="body2" fontWeight={700} textAlign="right">
                                        {ksh(loan.totalAmountDue)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={7}>
                                    <Typography variant="body2" color="success.main">
                                        Amount Paid
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography variant="body2" color="success.main" textAlign="right">
                                        {ksh(loan.amountPaid)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={7}>
                                    <Typography
                                        variant="body2"
                                        fontWeight={700}
                                        color={status === 'OVERDUE' ? 'error.main' : 'inherit'}
                                    >
                                        Outstanding
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography
                                        variant="body2"
                                        fontWeight={700}
                                        color={status === 'OVERDUE' ? 'error.main' : 'inherit'}
                                        textAlign="right"
                                    >
                                        {ksh(loan.amountOutstanding)}
                                    </Typography>
                                </Grid>

                                {loan.penaltyAmount > 0 && (
                                    <>
                                        <Grid item xs={7}>
                                            <Typography variant="body2" color="error.main">
                                                Penalty
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={5}>
                                            <Typography
                                                variant="body2"
                                                color="error.main"
                                                textAlign="right"
                                            >
                                                {ksh(loan.penaltyAmount)}
                                            </Typography>
                                        </Grid>
                                    </>
                                )}

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                </Grid>

                                <Grid item xs={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        Due Date
                                    </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography variant="body2" textAlign="right">
                                        {fmtDate(loan.dueDate)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                )}

                {/* Status messages for non-repayable states */}
                {status === 'PENDING_FEE_PAYMENT' && (
                    <>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Your application is awaiting the processing fee payment. An M-Pesa prompt was sent to your phone. If you didn't receive it or dismissed it, tap "Resend" below.
                        </Alert>
                        <Button
                            variant="outlined"
                            fullWidth
                            sx={{ ...S.actionBtn, mb: 2 }}
                            onClick={handleRetriggerFee}
                            disabled={retriggerCooldown > 0}
                            startIcon={retriggerCooldown > 0 ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {retriggerCooldown > 0
                                ? `Resend M-Pesa Prompt (${retriggerCooldown}s)`
                                : 'Resend M-Pesa Prompt'}
                        </Button>
                    </>
                )}
                {status === 'EXPIRED' && (
                    <>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            This application expired because the processing fee was not paid within 24 hours. You may apply for a new loan.
                        </Alert>
                        <Button
                            variant="contained"
                            fullWidth
                            sx={{ ...S.actionBtn, py: 1.5, mb: 2 }}
                            onClick={() => loansData?.kyc_status !== 'VERIFIED' ? loadKycDocuments() : handleLoadProducts()}
                            disabled={loading}
                        >
                            Apply Again
                        </Button>
                    </>
                )}
                {status === 'PENDING' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Your application is under review. A loan officer will process it shortly.
                    </Alert>
                )}
                {status === 'APPROVED' && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Your loan has been approved! Disbursement to your M-Pesa is in progress.
                    </Alert>
                )}
                {status === 'REJECTED' && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Your application was not approved. Please contact support for more information.
                    </Alert>
                )}
                {status === 'PAID' && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        This loan has been fully repaid. Well done — your credit score has improved!
                    </Alert>
                )}
                {status === 'DEFAULTED' && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        This loan has defaulted. Please contact support immediately to discuss
                        repayment options.
                    </Alert>
                )}

                {/* Repay button */}
                {canRepayLoan && (
                    <>
                        {status === 'OVERDUE' && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                This loan is past its due date. Penalties may be accruing. Please repay
                                immediately.
                            </Alert>
                        )}
                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            color={status === 'OVERDUE' ? 'error' : 'primary'}
                            sx={{ ...S.actionBtn, py: 1.5 }}
                            onClick={() => {
                                setRepayAmount(
                                    String(loan?.amountOutstanding || selectedLoan?.total_repayment || '')
                                );
                                setRepayDialog(true);
                            }}
                        >
                            Repay Now
                        </Button>
                    </>
                )}

                {/* Repay dialog */}
                <Dialog open={repayDialog} onClose={() => setRepayDialog(false)} maxWidth="xs" fullWidth>
                    <DialogTitle>Confirm Repayment</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            An M-Pesa prompt will be sent to <strong>{tenantPhone}</strong>. Enter
                            your M-Pesa PIN on your phone to complete the payment.
                        </Typography>
                        <TextField
                            label="Amount to Repay (KES)"
                            type="number"
                            value={repayAmount}
                            onChange={(e) => setRepayAmount(e.target.value)}
                            fullWidth
                            helperText={
                                loan ? `Outstanding balance: ${ksh(loan.amountOutstanding)}` : ''
                            }
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRepayDialog(false)}>Cancel</Button>
                        <Button
                            variant="contained"
                            color={status === 'OVERDUE' ? 'error' : 'primary'}
                            onClick={handleRepay}
                            disabled={
                                !repayAmount || parseFloat(repayAmount) <= 0 || loading
                            }
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            Send STK Push
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    // ── STK push waiting screen ────────────────────────────────────────────────
    if (view === 'repay_waiting') {
        const maskedPhone = tenantPhone.replace(/(\d{4})(\d{3})(\d{3})/, '$1 XXX $3');
        return (
            <Box sx={S.page}>
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <PhoneIcon sx={{ fontSize: 76, color: '#2563eb', mb: 2 }} />
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                        Check Your Phone
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 0.5 }}>
                        An M-Pesa prompt has been sent to
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
                        {maskedPhone}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        Enter your M-Pesa PIN to complete your{' '}
                        <strong>{ksh(repayResult?.amount)}</strong> repayment.
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            mb: 3,
                            color: 'text.secondary',
                        }}
                    >
                        <CircularProgress size={16} />
                        <Typography variant="body2">Waiting for confirmation…</Typography>
                    </Box>
                    <Alert severity="info" sx={{ mb: 4, textAlign: 'left' }}>
                        The loan status will update automatically after payment is confirmed. Tap
                        "Done" then pull-to-refresh on the loans list to see the latest status.
                    </Alert>
                    <Button
                        variant="contained"
                        size="large"
                        sx={{ ...S.actionBtn, px: 5 }}
                        onClick={() => loadLoans()}
                    >
                        Done
                    </Button>
                </Box>
            </Box>
        );
    }

    // ── Pay processing fee ─────────────────────────────────────────────────────
    if (view === 'pay_fee') {
        const maskedPhone = tenantPhone.replace(/(\d{4})(\d{3})(\d{3})/, '$1 XXX $3');

        // Countdown from created_at + 24h; payFeeNow forces a re-render each minute
        let countdown = null;
        if (feePaymentData?.created_at) {
            const expiryMs = new Date(feePaymentData.created_at).getTime() + 24 * 60 * 60 * 1000;
            const remainingMs = expiryMs - payFeeNow;
            if (remainingMs > 0) {
                const hours = Math.floor(remainingMs / (1000 * 60 * 60));
                const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
                countdown = `${hours}h ${minutes}m`;
            } else {
                countdown = 'Expired';
            }
        }

        return (
            <Box sx={S.page}>
                <Box sx={S.header}>
                    <Button sx={S.backBtn} onClick={() => loadLoans()}>
                        <ArrowBackIcon />
                    </Button>
                    <Typography variant="h5" fontWeight={700}>
                        Pay Processing Fee
                    </Typography>
                </Box>

                {formAlert && (
                    <Alert severity={formAlert.type} onClose={() => setFormAlert(null)} sx={{ mb: 2 }}>
                        {formAlert.message}
                    </Alert>
                )}

                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <PhoneIcon sx={{ fontSize: 76, color: '#eab308', mb: 2 }} />
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                        Check Your M-Pesa
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 0.5 }}>
                        A payment prompt of{' '}
                        <strong>{ksh(feePaymentData?.processing_fee)}</strong> has been sent to
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                        {maskedPhone}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                        Enter your M-Pesa PIN to pay the processing fee. Your loan application will proceed to review once payment is confirmed.
                    </Typography>

                    {countdown && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
                            <Typography variant="body2" color="text.secondary">⏱ Expires in</Typography>
                            <Typography
                                variant="body2"
                                fontWeight={700}
                                color={countdown === 'Expired' ? 'error.main' : 'text.primary'}
                            >
                                {countdown}
                            </Typography>
                        </Box>
                    )}

                    <Button
                        variant="outlined"
                        onClick={handleRetriggerFee}
                        disabled={retriggerCooldown > 0}
                        sx={{ ...S.actionBtn, mb: 1 }}
                        startIcon={retriggerCooldown > 0 ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {retriggerCooldown > 0
                            ? `Resend M-Pesa Prompt (${retriggerCooldown}s)`
                            : 'Resend M-Pesa Prompt'}
                    </Button>

                    {feePaymentData?.application_id && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
                            Application ID: {feePaymentData.application_id}
                        </Typography>
                    )}

                    <Alert severity="info" sx={{ textAlign: 'left', mb: 3 }}>
                        The application status updates automatically. You can also tap "Check Later" and return to the loans list — your application will still be waiting.
                    </Alert>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: 'text.secondary', mb: 3 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">Checking payment status every 15 seconds…</Typography>
                    </Box>

                    <Button variant="text" sx={S.actionBtn} onClick={() => loadLoans()}>
                        Check Later
                    </Button>
                </Box>
            </Box>
        );
    }

    // ── KYC document upload ────────────────────────────────────────────────────
    if (view === 'kyc_upload') {
        // A slot is "ready" if already uploaded/approved, using an existing doc, or has a new file
        const allReady = kycSlots.every(
            (s) => s.status === 'done' || s.mode === 'use_existing' || Boolean(s.file)
        );

        const handleFileChange = (type, file) => {
            if (!file) return;
            if (!KYC_ACCEPTED_TYPES.includes(file.type)) {
                setKycSlots((prev) =>
                    prev.map((s) =>
                        s.type === type
                            ? { ...s, file: null, error: 'Only JPEG, PNG, GIF, or PDF files are accepted.' }
                            : s
                    )
                );
                return;
            }
            if (file.size > KYC_MAX_SIZE) {
                setKycSlots((prev) =>
                    prev.map((s) =>
                        s.type === type
                            ? { ...s, file: null, error: 'File must be smaller than 10 MB.' }
                            : s
                    )
                );
                return;
            }
            setKycSlots((prev) =>
                prev.map((s) => (s.type === type ? { ...s, file, error: null } : s))
            );
        };

        return (
            <Box sx={S.page}>
                <Box sx={S.header}>
                    <Button sx={S.backBtn} onClick={() => setView('loans_list')}>
                        <ArrowBackIcon />
                    </Button>
                    <Typography variant="h5" fontWeight={700}>
                        KYC Document Upload
                    </Typography>
                </Box>

                {kycOverallStatus === 'PENDING' && kycSlots.some((s) => s.existing || s.status === 'done') ? (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        Your documents are under review by our team. You will be notified once
                        verification is complete. No action is required right now.
                    </Alert>
                ) : kycOverallStatus === 'REJECTED' ? (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        One or more documents were rejected. Please review the highlighted documents
                        below, upload a clearer copy, and re-submit.
                    </Alert>
                ) : (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        To apply for a loan, we need to verify your identity. Please provide all four
                        documents below. Documents already on file can be reused or replaced.
                    </Alert>
                )}

                {formAlert && (
                    <Alert severity={formAlert.type} onClose={() => setFormAlert(null)} sx={{ mb: 2 }}>
                        {formAlert.message}
                    </Alert>
                )}

                {kycSlots.map((slot) => (
                    <Card
                        key={slot.type}
                        sx={{
                            ...S.card,
                            border: slot.error ? '1px solid #ef4444' : '1px solid transparent',
                        }}
                    >
                        <CardContent>
                            {/* Slot header */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={700}>
                                        {slot.label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {slot.hint}
                                    </Typography>
                                </Box>
                                {slot.status === 'done' && (
                                    <Chip
                                        label="✓ Uploaded"
                                        size="small"
                                        sx={{ backgroundColor: '#d1fae5', color: '#065f46', fontWeight: 600 }}
                                    />
                                )}
                                {slot.status === 'uploading' && <CircularProgress size={20} />}
                            </Box>

                            {/* Existing document — use_existing mode */}
                            {slot.existing && slot.mode === 'use_existing' && slot.status !== 'done' && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip
                                        label="Document on file ✓"
                                        size="small"
                                        sx={{ backgroundColor: '#dbeafe', color: '#1e40af', fontWeight: 600 }}
                                    />
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() =>
                                            setKycSlots((prev) =>
                                                prev.map((s) =>
                                                    s.type === slot.type ? { ...s, mode: 'upload', file: null, error: null } : s
                                                )
                                            )
                                        }
                                    >
                                        Replace
                                    </Button>
                                </Box>
                            )}

                            {/* Upload mode */}
                            {slot.mode === 'upload' && slot.status !== 'done' && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Button component="label" variant="outlined" size="small">
                                        {slot.file ? 'Change File' : 'Choose File'}
                                        <input
                                            type="file"
                                            hidden
                                            accept=".jpg,.jpeg,.png,.gif,.pdf"
                                            onChange={(e) => handleFileChange(slot.type, e.target.files[0])}
                                        />
                                    </Button>
                                    {slot.file && (
                                        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {slot.file.name}
                                        </Typography>
                                    )}
                                    {/* Switch back to use_existing if the slot has an existing doc */}
                                    {slot.existing && slot.canUseExisting && (
                                        <Button
                                            size="small"
                                            variant="text"
                                            onClick={() =>
                                                setKycSlots((prev) =>
                                                    prev.map((s) =>
                                                        s.type === slot.type ? { ...s, mode: 'use_existing', file: null, error: null } : s
                                                    )
                                                )
                                            }
                                        >
                                            Use existing
                                        </Button>
                                    )}
                                </Box>
                            )}

                            {/* Inline error */}
                            {slot.error && (
                                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
                                    {slot.error}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                ))}

                <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={(kycOverallStatus === 'PENDING' && kycSlots.some((s) => s.existing || s.status === 'done')) || !allReady || kycSubmitting}
                    onClick={handleKycSubmit}
                    sx={{ ...S.actionBtn, py: 1.5 }}
                    startIcon={kycSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                >
                    {kycSubmitting
                        ? 'Uploading Documents…'
                        : kycOverallStatus === 'PENDING' && kycSlots.some((s) => s.existing || s.status === 'done')
                        ? 'Verification Pending — Under Review'
                        : kycOverallStatus === 'REJECTED'
                        ? 'Re-submit Rejected Documents'
                        : 'Submit Documents & Continue'}
                </Button>
            </Box>
        );
    }

    return null;
}
